import React, { useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

export const EXPLAINER_VIDEO = {
  src: "https://storage.motionflare.ai/prod/CmgGz116DJWvV35o3gm6CA80Y4u67hXx/919b9e81-e70f-425d-a4e4-b08f79a0d7c5/1920x1080-1080p-30fps-b48e844a-778c-4467-a6db-849b6a8cce5d.mp4",
  poster: "/explainer-poster.jpg",
  duration: "1:06",
};

export function ExplainerPlayer({ autoPlay = false, className = "" }) {
  return (
    <video src={EXPLAINER_VIDEO.src} poster={EXPLAINER_VIDEO.poster} controls autoPlay={autoPlay} playsInline preload="metadata"
      className={`w-full h-full bg-black ${className}`} data-testid="explainer-video" />
  );
}

export function ExplainerDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl p-0 overflow-hidden bg-black border-0 rounded-2xl" data-testid="explainer-dialog">
        <DialogTitle className="sr-only">Pitch Craft explainer video</DialogTitle>
        <div className="aspect-video">{open && <ExplainerPlayer autoPlay />}</div>
      </DialogContent>
    </Dialog>
  );
}

export function WatchExplainerButton({ className = "", variant = "outline", label = "Watch the 60-second tour", testId = "watch-explainer-btn" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button onClick={() => setOpen(true)} variant={variant} className={className} data-testid={testId}>
        <Play className="w-4 h-4 mr-2 fill-current" /> {label}
      </Button>
      <ExplainerDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

export function ExplainerSection() {
  const [playing, setPlaying] = useState(false);
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-testid="explainer-section">
      <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
        className="relative rounded-3xl p-2 sm:p-3 bg-white border border-slate-200 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.18)]">
        <div className="absolute -inset-6 bg-emerald-500/10 blur-3xl rounded-full pointer-events-none -z-10" />
        <div className="relative aspect-video rounded-2xl overflow-hidden bg-slate-900">
          {playing ? <ExplainerPlayer autoPlay /> : (
            <button type="button" onClick={() => setPlaying(true)} className="group absolute inset-0 w-full h-full text-left" aria-label="Play the Pitch Craft explainer video" data-testid="explainer-play-btn">
              <img src={EXPLAINER_VIDEO.poster} alt="Pitch Craft explainer video" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]" />
              <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-slate-900/75 to-transparent" />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="relative flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-emerald-500 text-black shadow-[0_10px_40px_rgba(16,185,129,0.55)] transition-transform duration-300 group-hover:scale-110">
                  <span className="absolute inset-0 rounded-full bg-emerald-400/60 animate-ping" style={{ animationDuration: "2.2s" }} />
                  <Play className="relative w-9 h-9 sm:w-11 sm:h-11 ml-1 fill-current" />
                </span>
              </div>
              <div className="absolute left-5 right-5 bottom-5 sm:left-7 sm:bottom-7 flex flex-wrap items-end justify-between gap-3">
                <div>
                  <p className="text-[11px] font-mono uppercase tracking-widest text-emerald-300">See it in action</p>
                  <p className="text-white font-bold text-lg sm:text-2xl tracking-tight mt-1">Pitch Craft in 60 seconds</p>
                </div>
                <span className="text-xs font-mono text-white/80 bg-white/10 backdrop-blur px-2.5 py-1 rounded-full border border-white/20">{EXPLAINER_VIDEO.duration}</span>
              </div>
            </button>
          )}
        </div>
      </motion.div>
    </section>
  );
}
